"use client";

import * as React from "react";
import { usePathname, useRouter } from "next/navigation";

import type { Blog } from "contentlayer/generated";

import { formatDate, getMonth, getDay } from "@/lib/utils";
import { Tag, ListSection, ListItem } from "@/components";

type ArticlesByYear = Record<string, Blog[]>;

export default function ArticleList({ items }: { items: Blog[] }) {
    const [selectedTag, setSelectedTag] = React.useState<string | null>(null);

    const router = useRouter();
    const pathname = usePathname();

    const allTags = Array.from(
        new Set(items.flatMap((post) => post.tags?.split(", ").filter(Boolean) || [])),
    );

    const handleTagChange = (tag: string | null) => {
        setSelectedTag(tag);

        // Update the URL parameter
        if (tag) {
            router.push(`${pathname}?tag=${tag}`);
        } else {
            router.push(pathname);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    React.useEffect(() => {
        // Check if 'tag' parameter exists in the URL
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("tag")) {
            setSelectedTag(urlParams.get("tag"));
        }
    }, [pathname]);

    // Filter articles by the selected tag
    const filteredItems = items.filter(
        (post) => post.isPublished && (selectedTag ? post.tags?.includes(selectedTag) : true),
    );

    // Group filtered articles by year
    const articlesByYear = filteredItems.reduce((acc: ArticlesByYear, item) => {
        const year = new Date(item.publishedAt).getFullYear().toString();
        if (!acc[year]) {
            acc[year] = [];
        }
        acc[year].push(item);
        return acc;
    }, {} as ArticlesByYear);

    const MonthDay = ({ month, day }: { month: string; day: number }) => {
        const formattedDay = day.toString().padStart(2, "0");

        return (
            <div className="mr-2 flex flex-col items-center justify-start text-base-foreground text-sm uppercase">
                <span className="font-bold font-mono text-xs leading-5">{month}</span>
                <span className="font-mono font-semibold text-lg leading-5">{formattedDay}</span>
            </div>
        );
    };

    return (
        <div className="block">
            {/* List of tags */}
            <section>
                <h2 className="sr-only">Tags</h2>
                <ul className="flex gap-x-3">
                    <li key="all">
                        <Tag onClick={() => handleTagChange(null)}>#all</Tag>
                    </li>

                    {allTags.map((tag) => (
                        <li key={tag}>
                            <Tag onClick={() => handleTagChange(tag)}>{`#${tag}`}</Tag>
                        </li>
                    ))}
                </ul>
            </section>

            <hr className="my-6 border-base" />

            {/* List of articles by year */}
            {Object.keys(articlesByYear)
                .sort((a, b) => b.localeCompare(a))
                .map((year) => (
                    <React.Fragment key={year}>
                        <ListSection title={year}>
                            {articlesByYear[year]
                                .filter((post) =>
                                    selectedTag ? post.tags?.includes(selectedTag) : true,
                                )
                                .sort(
                                    (a, b) =>
                                        new Date(b.publishedAt).getTime() -
                                        new Date(a.publishedAt).getTime(),
                                )
                                .map((post, index, array) => {
                                    const month = getMonth(post.publishedAt);
                                    const day = getDay(post.publishedAt);
                                    const formattedDate = formatDate(post.publishedAt);

                                    if (!month || !day) return null;

                                    return (
                                        <ListItem
                                            key={post.slug}
                                            title={post.title}
                                            description={formattedDate} // <ViewCounter slug={post.slug} trackView={false} />
                                            left={() => <MonthDay month={month} day={day} />}
                                            href={`/${post.slug}`}
                                            isLastItem={index === array.length - 1}
                                        />
                                    );
                                })}
                        </ListSection>
                    </React.Fragment>
                ))}
        </div>
    );
}
